import { cn } from '@/lib/utils'
import React from 'react'

interface BreadcrumbItem {
  label: string
  href?: string
  onClick?: () => void
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
  className?: string
  separator?: React.ReactNode
  onNavigate?: (href: string) => void
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items, className, separator, onNavigate, ...props }, ref) => {
    const handleItemClick = (item: BreadcrumbItem, e: React.MouseEvent) => {
      if (item.onClick) {
        e.preventDefault()
        item.onClick()
      } else if (item.href && onNavigate) {
        e.preventDefault()
        onNavigate(item.href)
      }
    }

    return (
      <nav
        ref={ref}
        aria-label='Breadcrumb'
        className={cn(
          'flex items-center font-neue-machina text-sm font-light tracking-wide',
          className,
        )}
        {...props}
      >
        <BreadcrumbList>
          {items.map((item, index) => (
            <React.Fragment key={index}>
              <BreadcrumbItem>
                {index === items.length - 1 ? (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    title={item.label}
                    href={item.href}
                    onClick={(e) => handleItemClick(item, e)}
                  >
                    {item.label}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {index < items.length - 1 && (
                <BreadcrumbSeparator>
                  {separator || <span className='px-2 text-neutral-500'>/</span>}
                </BreadcrumbSeparator>
              )}
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </nav>
    )
  },
)
Breadcrumb.displayName = 'Breadcrumb'

const BreadcrumbList = React.forwardRef<HTMLOListElement, React.ComponentPropsWithoutRef<'ol'>>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn('flex flex-wrap items-center gap-1.5 break-words text-neutral-500', className)}
      {...props}
    />
  ),
)
BreadcrumbList.displayName = 'BreadcrumbList'

const BreadcrumbItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<'li'>>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn('inline-flex items-center gap-1.5', className)} {...props} />
  ),
)
BreadcrumbItem.displayName = 'BreadcrumbItem'

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, React.ComponentPropsWithoutRef<'a'>>(
  ({ className, ...props }, ref) => (
    <a
      ref={ref}
      className={cn(
        '-mx-1 -my-0.5 max-w-[12ch] cursor-pointer truncate rounded-sm px-1 py-0.5 text-neutral-300 transition-colors hover:text-neutral-400 focus:outline-none',
        className,
      )}
      {...props}
    />
  ),
)
BreadcrumbLink.displayName = 'BreadcrumbLink'

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<'span'>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      role='link'
      aria-disabled='true'
      aria-current='page'
      className={cn('font-neue-machina text-sm font-light tracking-wide', className)}
      {...props}
    />
  ),
)
BreadcrumbPage.displayName = 'BreadcrumbPage'

const BreadcrumbSeparator = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<'li'>>(
  ({ className, children, ...props }, ref) => (
    <li
      ref={ref}
      role='presentation'
      aria-hidden='true'
      className={cn('text-neutral-300', className)}
      {...props}
    >
      {children}
    </li>
  ),
)
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'

export {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  type BreadcrumbItem as BreadcrumbItemType,
  type BreadcrumbProps,
}
